/**
 * The last N numbers, oldest first, in a buffer that is allocated once and never emptied.
 *
 * Frame times and pings are both read by something other than whatever writes them — the
 * perf graph, the e2e probe, the low-frame-rate warning — and on its own schedule. The
 * frame times used to live in an array that `trackPerf` cleared every 240 frames, so a read
 * that landed in the frame after a clear found nothing and reported 0 fps: the meter
 * dropped to zero and back on a perfectly smooth race, about once every four seconds at
 * 60 Hz. A ring that only ever overwrites its oldest sample has no such moment.
 */
export class SampleRing {
  private readonly values: Float64Array;
  private next = 0;
  private count = 0;

  constructor(readonly capacity: number) {
    this.values = new Float64Array(Math.max(1, Math.floor(capacity)));
  }

  push(value: number): void {
    this.values[this.next] = value;
    this.next = (this.next + 1) % this.values.length;
    if (this.count < this.values.length) this.count++;
  }

  get length(): number {
    return this.count;
  }

  /** The `i`th sample, 0 being the oldest still held. */
  at(i: number): number {
    const start = (this.next - this.count + this.values.length) % this.values.length;
    return this.values[(start + i) % this.values.length];
  }

  /** The newest sample, or `fallback` before there is one. */
  latest(fallback = 0): number {
    return this.count === 0 ? fallback : this.at(this.count - 1);
  }

  /** Mean of the newest `window` samples (all of them by default); 0 when empty. */
  mean(window = this.count): number {
    const n = Math.min(Math.max(0, Math.floor(window)), this.count);
    if (n === 0) return 0;
    let sum = 0;
    for (let i = this.count - n; i < this.count; i++) sum += this.at(i);
    return sum / n;
  }

  /** Largest of the newest `window` samples; 0 when empty. */
  max(window = this.count): number {
    const n = Math.min(Math.max(0, Math.floor(window)), this.count);
    let best = 0;
    for (let i = this.count - n; i < this.count; i++) best = Math.max(best, this.at(i));
    return best;
  }
}

/**
 * Frames per second from frame times in seconds, over the newest `window` frames.
 *
 * Averaged as time rather than as rates — `n / Σdt`, not `Σ(1/dt) / n` — so one long
 * frame weighs what it actually cost; the mean of rates lets a single 200 ms hitch vanish
 * among a hundred fast frames.
 */
export function fpsOf(frameTimes: SampleRing, window: number): number {
  const mean = frameTimes.mean(window);
  return mean > 0 ? 1 / mean : 0;
}
